import React, { useState, useEffect, useMemo } from 'react';
import './HierarchicalProcedureSelector.css';

// API configuration - Railway uses full URL, Codespace uses relative URLs with proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface StandardProcedure {
  id: number;
  name: string;
  description: string;
  standard_sop: string;
  keywords: string[];
}

interface ProcedureCategory {
  name: string;
  displayName: string;
  procedures: StandardProcedure[];
  totalCount: number;
}

interface HierarchicalProcedureSelectorProps {
  protocolId: number;
  onSelectionChange: (selectedIds: number[], selectedProcedures: StandardProcedure[]) => void;
  onGenerateText: () => void;
  isLoading?: boolean;
}

const HierarchicalProcedureSelector: React.FC<HierarchicalProcedureSelectorProps> = ({
  protocolId,
  onSelectionChange,
  onGenerateText,
  isLoading = false
}) => {
  const [categories, setCategories] = useState<ProcedureCategory[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [loadingStandard, setLoadingStandard] = useState(true);
  const [loadingSelected, setLoadingSelected] = useState(true);

  // Load standard procedures from backend
  useEffect(() => {
    const loadStandardProcedures = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        // Check if user is authenticated
        if (!token) {
          console.warn('No authentication token found - user needs to login');
          setLoadingStandard(false);
          return;
        }
        
        const response = await fetch(`${API_BASE_URL}/api/procedures/standard`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 403 || response.status === 401) {
            console.warn('Authentication failed - user needs to login again');
            // Clear invalid token
            localStorage.removeItem('auth_token');
          }
          throw new Error('Failed to load procedures');
        }
        
        const data = await response.json();
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Error loading standard procedures:', error);
      } finally {
        setLoadingStandard(false);
      }
    };

    loadStandardProcedures();
  }, []);

  // Load selected procedures for this protocol
  useEffect(() => {
    if (!protocolId) return;

    const loadSelectedProcedures = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${API_BASE_URL}/api/procedures/selected/${protocolId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSelectedIds(new Set(data.selectedIds));
        }
      } catch (error) {
        console.error('Error loading selected procedures:', error);
      } finally {
        setLoadingSelected(false);
      }
    };

    loadSelectedProcedures();
  }, [protocolId]);

  // Filter procedures based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm && !showOnlySelected) return categories;

    return categories.map(category => {
      let filteredProcedures = category.procedures;

      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filteredProcedures = filteredProcedures.filter(proc =>
          proc.name.toLowerCase().includes(search) ||
          (proc.description && proc.description.toLowerCase().includes(search)) ||
          proc.keywords.some(keyword => keyword.toLowerCase().includes(search))
        );
      }

      // Apply "show only selected" filter
      if (showOnlySelected) {
        filteredProcedures = filteredProcedures.filter(proc => 
          selectedIds.has(proc.id)
        );
      }

      return {
        ...category,
        procedures: filteredProcedures,
        selectedCount: filteredProcedures.filter(p => selectedIds.has(p.id)).length
      };
    }).filter(category => category.procedures.length > 0);
  }, [categories, searchTerm, showOnlySelected, selectedIds]);

  // Handle procedure selection
  const handleProcedureToggle = (procedureId: number) => {
    const newSelectedIds = new Set(selectedIds);
    
    if (newSelectedIds.has(procedureId)) {
      newSelectedIds.delete(procedureId);
    } else {
      newSelectedIds.add(procedureId);
    }
    
    setSelectedIds(newSelectedIds);
    
    // Get all selected procedures
    const allSelectedProcedures: StandardProcedure[] = [];
    categories.forEach(category => {
      category.procedures.forEach(proc => {
        if (newSelectedIds.has(proc.id)) {
          allSelectedProcedures.push(proc);
        }
      });
    });
    
    onSelectionChange(Array.from(newSelectedIds), allSelectedProcedures);
  };

  // Handle category collapse/expand
  const handleCategoryToggle = (categoryName: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(categoryName)) {
      newCollapsed.delete(categoryName);
    } else {
      newCollapsed.add(categoryName);
    }
    setCollapsedCategories(newCollapsed);
  };

  // Handle select all in category
  const handleSelectAllInCategory = (category: ProcedureCategory) => {
    const newSelectedIds = new Set(selectedIds);
    const categoryProcedureIds = category.procedures.map(p => p.id);
    
    // Check if all procedures in category are selected
    const allSelected = categoryProcedureIds.every(id => selectedIds.has(id));
    
    if (allSelected) {
      // Deselect all in category
      categoryProcedureIds.forEach(id => newSelectedIds.delete(id));
    } else {
      // Select all in category
      categoryProcedureIds.forEach(id => newSelectedIds.add(id));
    }
    
    setSelectedIds(newSelectedIds);
    
    // Get all selected procedures
    const allSelectedProcedures: StandardProcedure[] = [];
    categories.forEach(cat => {
      cat.procedures.forEach(proc => {
        if (newSelectedIds.has(proc.id)) {
          allSelectedProcedures.push(proc);
        }
      });
    });
    
    onSelectionChange(Array.from(newSelectedIds), allSelectedProcedures);
  };

  if (loadingStandard) {
    return <div className="procedure-selector-loading">Loading standard procedures...</div>;
  }

  const totalSelected = selectedIds.size;
  const totalAvailable = categories.reduce((sum, cat) => sum + cat.totalCount, 0);

  if (categories.length === 0) {
    const token = localStorage.getItem('auth_token');
    return (
      <div className="procedure-selector-error">
        {!token ? (
          <p>Please log in to view and select procedures.</p>
        ) : (
          <>
            <p>No procedures loaded. Check console for errors.</p>
            <p>Expected {totalAvailable} procedures across categories.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="hierarchical-procedure-selector">
      {/* Search and filter controls */}
      <div className="search-section">
        <input
          type="text"
          className="procedure-search-input"
          placeholder="Search procedures..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <label className="show-selected-checkbox">
          <input
            type="checkbox"
            checked={showOnlySelected}
            onChange={(e) => setShowOnlySelected(e.target.checked)}
          />
          Show only selected ({totalSelected})
        </label>
      </div>

      {/* Categories */}
      <div className="procedure-categories">
        {filteredCategories.map(category => {
          const selectedInCategory = category.procedures.filter(p => selectedIds.has(p.id)).length;
          const isCollapsed = collapsedCategories.has(category.name);
          const allSelected = selectedInCategory === category.procedures.length && category.procedures.length > 0;
          const someSelected = selectedInCategory > 0;

          return (
            <div key={category.name} className="procedure-category">
              <div className="category-header">
                <button
                  className="category-toggle"
                  onClick={() => handleCategoryToggle(category.name)}
                >
                  {isCollapsed ? '▶' : '▼'}
                </button>
                
                <h4 className="category-title">
                  {category.displayName}
                  <span className="category-count">
                    ({selectedInCategory}/{category.procedures.length})
                  </span>
                </h4>
                
                <button
                  className={`select-all-button ${allSelected ? 'all-selected' : someSelected ? 'some-selected' : ''}`}
                  onClick={() => handleSelectAllInCategory(category)}
                  title={allSelected ? 'Deselect all' : 'Select all'}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              {!isCollapsed && (
                <div className="category-procedures">
                  {category.procedures.map(procedure => (
                    <div key={procedure.id} className="procedure-item">
                      <label className="procedure-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(procedure.id)}
                          onChange={() => handleProcedureToggle(procedure.id)}
                        />
                        <div className="procedure-info">
                          <div className="procedure-name">{procedure.name}</div>
                          {procedure.description && (
                            <div className="procedure-description">
                              {procedure.description}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="procedure-selector-actions">
        <button 
          className="generate-text-button"
          onClick={onGenerateText}
          disabled={isLoading || totalSelected === 0}
        >
          {isLoading ? 'Generating...' : `Generate Q4.4 Text (${totalSelected} procedures)`}
        </button>
        
        <div className="help-text">
          Select procedures above to automatically generate descriptions for Question 4.4
        </div>
      </div>
    </div>
  );
};

export default HierarchicalProcedureSelector;